import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import InputRange from 'react-input-range';
import PaymentDetailsRow from '../payment-details-row';
import {
  addCurrencySymbolToPrice,
  formatPrice,
  formatPriceWithCurrencySymbol,
  interpolate
} from '../../../../../../app/functions';
import 'react-input-range/lib/css/index.css';
import './index.scss';

const PaymentDetailsRangeRow = ({
  value,
  error = null,
  onValueChange = () => {},
  minimumValue = 0,
  maximumValue = 0,
  stepValue = 0,
  monthlyLimit = 0,
}) => {
  // Redux
  const translations = useSelector((store) => store.config.Translations);
  const disabled = minimumValue === maximumValue;
  // Row State
  const [inputValue, setInputValue] = useState(value);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // References
  const inputRef = useRef(null);

  const preventScroll = (e) => {
    e.target.blur();
    e.stopPropagation();
    setTimeout(e.target.focus, 0);
  };

  // --------------------------------
  // Row Functions
  // --------------------------------

  const handleInputChange = ({ target }) => {
    setInputValue(prev => (isNaN(parseFloat(target.value)) ? prev : target.value));
  };

  const handleInputFocusIn = () => setIsInputFocused(true);

  const handleInputFocusOut = (event) => {
    const shouldFocusOut = event.type === 'blur' || (
      event.type === 'mousedown' && event.target.tagName !== 'INPUT'
    ) || (
      event.type === 'keydown' && event.key === 'Enter'
    );

    if (shouldFocusOut) {
      setIsInputFocused(false);
      inputRef.current && inputRef.current.blur();
    }
  };

  // --------------------------------
  // Row Effects
  // --------------------------------

  useEffect(() => setInputValue(value), [value]);

  useLayoutEffect(() => {
    if (isInputFocused) {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }

      window.addEventListener('mousedown', handleInputFocusOut);
      window.addEventListener('keydown', handleInputFocusOut);

      return () => {
        window.removeEventListener('mousedown', handleInputFocusOut);
        window.removeEventListener('keydown', handleInputFocusOut);
      };
    } else {
      onValueChange(inputValue) || setInputValue(value);
    }
  }, [isInputFocused]);

  return (
    <>
      <PaymentDetailsRow
        label={translations['payment_choose_purchase_voucher_value']}
        subtitle={interpolate(translations['voucher_slider_budget_monthly_limit'], {monthlyLimit: formatPrice(monthlyLimit, 0)})}
        value={(
          <input
            type={isInputFocused ? 'number' : 'text'}
            value={isInputFocused ? inputValue : formatPriceWithCurrencySymbol(inputValue, 'ILS', 0)}
            onChange={handleInputChange}
            onWheel={preventScroll}
            onFocus={handleInputFocusIn}
            onBlur={handleInputFocusOut}
            disabled={minimumValue === maximumValue}
            min={minimumValue}
            max={maximumValue}
            step={stepValue}
            className="payment-details-range-value"
            ref={inputRef}
            pattern={'[0-9]*'}
            inputMode={'numeric'}
          />
        )}
        error={error}
      />

      {!disabled && <div className="payment-details-range-field">
        <InputRange
            value={Number(value)}
            onChange={onValueChange}
            minValue={minimumValue}
            maxValue={maximumValue}
            disabled={disabled}
            step={stepValue}
            formatLabel={(value) => addCurrencySymbolToPrice(formatPrice(value, 0))}
        />
      </div>}
    </>
  );
};

export default PaymentDetailsRangeRow;
